
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function PricingPage() {
  const plans = [
    {
      name: 'Starter',
      price: 199,
      description: 'Perfect for new agents getting started with AI',
      features: [
        'AI Chatbot (Basic)',
        'Lead Generation (100/month)',
        'Email Support',
        'Basic Analytics',
        '1 Property Dashboard',
        'Social Media AI (10 posts/month)'
      ],
      popular: false,
      cta: 'Start Free Trial'
    },
    {
      name: 'Professional',
      price: 499,
      description: 'Ideal for established agents scaling their business',
      features: [
        'AI Clone Service',
        'Advanced Chatbots',
        'Lead Generation (500/month)',
        'Workflow Automation',
        'AI Property Staging (20/month)',
        'Social Media AI (50 posts/month)',
        'Market Analytics',
        'Priority Support',
        '5 Property Dashboards'
      ],
      popular: true,
      cta: 'Start Free Trial'
    },
    {
      name: 'Enterprise',
      price: 999,
      description: 'For teams and brokerages needing full AI suite',
      features: [
        'Everything in Professional',
        'Unlimited Lead Generation',
        'Custom AI Training',
        'White-label Solutions',
        'Unlimited Property Staging',
        'Advanced Market Insights',
        'Dedicated Account Manager',
        'Custom Integrations',
        'Unlimited Dashboards',
        'Team Management'
      ],
      popular: false,
      cta: 'Contact Sales'
    }
  ];

  const addOns = [
    {
      name: 'Additional AI Staging',
      price: 49,
      description: 'Extra property staging credits beyond your plan limit',
      unit: 'per 10 stagings'
    },
    {
      name: 'Premium Market Data',
      price: 99,
      description: 'Advanced market analytics and predictive insights',
      unit: 'per month'
    },
    {
      name: 'Custom AI Training',
      price: 299,
      description: 'Personalized AI training for your specific market',
      unit: 'one-time setup'
    },
    {
      name: 'API Access',
      price: 199,
      description: 'Full API access for custom integrations',
      unit: 'per month'
    }
  ];

  const faqs = [
    {
      question: 'Can I change my plan anytime?',
      answer: 'Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately and billing is prorated.'
    },
    {
      question: 'Is there a free trial?',
      answer: 'Yes, we offer a 14-day free trial for all plans. No credit card required to start.'
    },
    {
      question: 'What happens if I exceed my limits?',
      answer: 'You can purchase additional credits or upgrade to a higher plan. We\'ll notify you before you reach your limits.'
    },
    {
      question: 'Do you offer custom enterprise solutions?',
      answer: 'Yes, we provide custom solutions for large teams and brokerages. Contact our sales team for a personalized quote.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Absolutely. We use enterprise-grade security and comply with all real estate industry regulations and data protection standards.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Choose the perfect plan for your real estate business. All plans include our core AI features with no hidden fees.
          </p>
          <div className="flex justify-center items-center space-x-4 mb-8">
            <span className="text-blue-200">Monthly</span>
            <div className="relative">
              <input type="checkbox" className="sr-only" />
              <div className="w-12 h-6 bg-blue-500 rounded-full shadow-inner"></div>
              <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition"></div>
            </div>
            <span className="text-white font-semibold">Annual (Save 20%)</span>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <div key={index} className={`relative bg-white rounded-2xl shadow-xl overflow-hidden ${plan.popular ? 'ring-2 ring-blue-600 scale-105' : ''}`}>
                {plan.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-blue-600 text-white text-center py-2 text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                
                <div className={`p-8 ${plan.popular ? 'pt-12' : ''}`}>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-6">{plan.description}</p>
                  
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                    <span className="text-gray-600">/month</span>
                  </div>
                  
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-3">
                        <i className="ri-check-line text-green-500 text-lg"></i>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link to={plan.name === 'Enterprise' ? '/contact' : '/demo'}>
                    <Button 
                      className={`w-full ${plan.popular ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                      variant={plan.popular ? 'primary' : 'outline'}
                    >
                      {plan.cta}
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Add-ons & Extras
            </h2>
            <p className="text-xl text-gray-600">
              Enhance your plan with additional features and services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {addOns.map((addon, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{addon.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{addon.description}</p>
                <div className="text-2xl font-bold text-blue-600 mb-1">${addon.price}</div>
                <div className="text-gray-500 text-sm">{addon.unit}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Feature Comparison
            </h2>
            <p className="text-xl text-gray-600">
              See what's included in each plan
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full max-w-6xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900">Features</th>
                  <th className="text-center py-4 px-6 font-semibold text-gray-900">Starter</th>
                  <th className="text-center py-4 px-6 font-semibold text-gray-900">Professional</th>
                  <th className="text-center py-4 px-6 font-semibold text-gray-900">Enterprise</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="py-4 px-6 font-medium">AI Clone Service</td>
                  <td className="py-4 px-6 text-center"><i className="ri-close-line text-red-500"></i></td>
                  <td className="py-4 px-6 text-center"><i className="ri-check-line text-green-500"></i></td>
                  <td className="py-4 px-6 text-center"><i className="ri-check-line text-green-500"></i></td>
                </tr>
                <tr>
                  <td className="py-4 px-6 font-medium">Lead Generation</td>
                  <td className="py-4 px-6 text-center">100/month</td>
                  <td className="py-4 px-6 text-center">500/month</td>
                  <td className="py-4 px-6 text-center">Unlimited</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 font-medium">AI Property Staging</td>
                  <td className="py-4 px-6 text-center">5/month</td>
                  <td className="py-4 px-6 text-center">20/month</td>
                  <td className="py-4 px-6 text-center">Unlimited</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 font-medium">Market Analytics</td>
                  <td className="py-4 px-6 text-center">Basic</td>
                  <td className="py-4 px-6 text-center">Advanced</td>
                  <td className="py-4 px-6 text-center">Premium</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 font-medium">Support</td>
                  <td className="py-4 px-6 text-center">Email</td>
                  <td className="py-4 px-6 text-center">Priority</td>
                  <td className="py-4 px-6 text-center">Dedicated</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Get answers to common questions about our pricing and plans
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Transform Your Real Estate Business?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Start your free trial today and see how AI can revolutionize your lead generation and sales process.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/demo">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
                <i className="ri-rocket-fill mr-2"></i>
                Start Free Trial
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-600">
                <i className="ri-phone-fill mr-2"></i>
                Talk to Sales
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
